import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHeart, FiUsers, FiTrendingUp, FiShield, FiTarget, FiStar, FiPlay, FiGlobe, FiArrowRight, FiCheckCircle } = FiIcons;

const CreatorsSection = () => {
  const creatorTypes = [
    {
      icon: FiPlay,
      title: 'LGBTQ+ YouTubers & Streamers',
      description: 'Content creators building educational, entertainment, and advocacy channels that need professional growth strategies and community engagement.',
      services: ['Channel optimization', 'Content strategy', 'Community building', 'Monetization support']
    },
    {
      icon: FiUsers,
      title: 'Drag Performers & Artists',
      description: 'NYC drag queens, kings, and non-binary performers looking to expand their digital presence and create sustainable income streams.',
      services: ['OTT channel creation', 'Social media management', 'Fan engagement platforms', 'Event promotion']
    },
    {
      icon: FiHeart,
      title: 'Queer-Owned Businesses',
      description: 'LGBTQ+ entrepreneurs and business owners who want to authentically connect with their community and grow their digital presence.',
      services: ['Brand development', 'Social media strategy', 'Community platforms', 'Customer engagement']
    },
    {
      icon: FiGlobe,
      title: 'Community Organizations',
      description: 'LGBTQ+ nonprofits, advocacy groups, and community centers that need digital platforms to reach, engage, and mobilize their audiences.',
      services: ['Website development', 'Community platforms', 'Event streaming', 'Donor engagement tools']
    }
  ];

  const successStories = [
    {
      type: 'Trans Rights Advocate',
      growth: '400% follower increase',
      platform: 'Multi-platform presence',
      quote: 'CSM helped me turn my advocacy into a sustainable career while staying true to my mission.'
    },
    {
      type: 'Brooklyn Drag Collective',
      growth: '50K+ OTT subscribers',
      platform: 'Custom streaming channel',
      quote: 'Having our own streaming platform changed everything. We own our content and our community.'
    },
    {
      type: 'Queer Wellness Coach',
      growth: '300% revenue increase',
      platform: 'Community membership site',
      quote: 'The membership platform CSM built helps me serve my community while building recurring revenue.'
    }
  ];

  const challenges = [
    {
      icon: FiShield,
      title: 'Algorithm Dependency',
      problem: 'Relying solely on social media algorithms for audience reach',
      solution: 'Multi-platform strategy with owned channels and communities'
    },
    {
      icon: FiTarget,
      title: 'Monetization Struggles',
      problem: 'Difficulty turning passion and audience into sustainable income',
      solution: 'Diverse revenue streams across platforms and direct fan support'
    },
    {
      icon: FiUsers,
      title: 'Community Building',
      problem: 'Scattered audience across multiple platforms with no central hub',
      solution: 'Custom community platforms that bring your audience together'
    }
  ];

  return (
    <section id="creators" className="py-20 lg:py-32 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Built for NYC's
            </span>
            <br />
            <span className="text-white">LGBTQ+ Creative Community</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Whether you're a drag performer in Brooklyn, a trans rights advocate in Manhattan, or a queer-owned 
            business in Queens, Create Space Media understands the unique challenges and opportunities facing 
            LGBTQ+ content creators in New York City. We're here to help you build sustainable digital presence 
            that honors your authentic voice while growing your community and income.
          </p>
        </motion.div>

        {/* Creator Types */}
        <div className="mb-20">
          <motion.h3
            className="text-3xl lg:text-4xl font-bold text-center mb-12 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Who We Serve
          </motion.h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            {creatorTypes.map((type, index) => (
              <motion.div
                key={type.title}
                className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 hover:border-purple-500/50 transition-all duration-300 group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center space-x-4 mb-6">
                  <SafeIcon icon={type.icon} className="text-3xl text-purple-400 group-hover:text-pink-400 transition-colors duration-300" />
                  <h4 className="text-xl font-bold text-white">{type.title}</h4>
                </div>
                <p className="text-gray-300 leading-relaxed mb-6">{type.description}</p>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-purple-400 mb-3">Our Services Include:</p>
                  {type.services.map((service, idx) => (
                    <div key={service} className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheckCircle} className="text-pink-400 text-sm" />
                      <span className="text-gray-300 text-sm">{service}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Challenges & Solutions */}
        <div className="mb-20">
          <motion.h3
            className="text-3xl lg:text-4xl font-bold text-center mb-12 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Common Creator Challenges We Solve
          </motion.h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {challenges.map((challenge, index) => (
              <motion.div
                key={challenge.title}
                className="text-center group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700 group-hover:border-purple-500/50 rounded-2xl p-8 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10">
                  <SafeIcon icon={challenge.icon} className="text-5xl text-purple-400 mx-auto mb-6 group-hover:text-pink-400 transition-colors duration-300" />
                  <h4 className="text-xl font-bold text-white mb-4">{challenge.title}</h4>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-red-400 mb-2">The Problem:</p>
                      <p className="text-gray-300 text-sm leading-relaxed">{challenge.problem}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-green-400 mb-2">Our Solution:</p>
                      <p className="text-gray-300 text-sm leading-relaxed">{challenge.solution}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Success Stories */}
        <div className="mb-20">
          <motion.h3
            className="text-3xl lg:text-4xl font-bold text-center mb-12 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Creator Success Stories
          </motion.h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {successStories.map((story, index) => (
              <motion.div
                key={story.type}
                className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 backdrop-blur-sm border border-purple-500/20 rounded-2xl p-8"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-center mb-6">
                  <SafeIcon icon={FiStar} className="text-3xl text-yellow-400 mx-auto mb-4" />
                  <h4 className="text-lg font-bold text-white mb-2">{story.type}</h4>
                  <div className="space-y-1">
                    <p className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                      {story.growth}
                    </p>
                    <p className="text-sm text-purple-400">{story.platform}</p>
                  </div>
                </div>
                <blockquote className="text-gray-300 italic text-center leading-relaxed">
                  "{story.quote}"
                </blockquote>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          className="text-center bg-gradient-to-r from-purple-900/30 to-pink-900/30 backdrop-blur-sm border border-purple-500/20 rounded-3xl p-8 lg:p-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="text-3xl lg:text-4xl font-bold mb-6 text-white">
            Ready to Own Your Digital Space?
          </h3>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto mb-8 leading-relaxed">
            Join hundreds of LGBTQ+ content creators in NYC who've built sustainable, community-driven 
            digital presence with Create Space Media. Let's create something amazing together.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <motion.button
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-purple-500/25 transition-all duration-200 flex items-center space-x-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Work With a NYC Media Agency That Gets You</span>
              <SafeIcon icon={FiArrowRight} />
            </motion.button>
          </div>
          <p className="text-sm text-gray-400 mt-6">
            Free consultation • NYC-based team • LGBTQ+ owned and operated
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default CreatorsSection;